import { get, post, put, remove } from "./api";
import { Rule, SubmitRuleForm } from "./NetTypes";

export const getRules = async (project: string): Promise<Rule[]> => {
  const response = await get({ path: `/rules?projectId=${project}` });
  return response.data;
};

export const saveRule = async (rule: SubmitRuleForm): Promise<Rule> => {
  const response = await post({ path: `/rules`, data: rule });
  return response.data;
};

export const getRule = async (ruleId: string): Promise<any> => {
  const response = await get({ path: `/rules/${ruleId}` });
  return response.data;
};

export const deleteRule = async (ruleId: string): Promise<any> => {
  const response = await remove({ path: `/rules/${ruleId}` });
  return response.data;
};

export const enableRule = async (ruleId: string): Promise<any> => {
  const response = await put({ path: `/rules/${ruleId}/enable`, data: {} });
  return response.data;
};
/** Paged rule run history (run list). */
export interface RuleLogsPageDto {
  content: RuleActivityLog[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export const getRuleLogs = async (
  ruleId: string,
  page = 0,
  size = 20
): Promise<RuleLogsPageDto> => {
  const response = await get({
    path: `/rules/${ruleId}/logs`,
    params: { page, size },
  });
  return response.data;
};

/** Single rule run log with steps and metrics (detail view). */
export interface RuleRunLogDetail {
  id: string;
  ruleId: string;
  itemId: string;
  itemName: string;
  itemType: string;
  projectId: string;
  createdAt: string;
  jobState: string;
  jobMessage: string;
  steps: RuleRunStepDto[];
  metrics: RuleRunMetricsDto;
}

export interface RuleRunStepDto {
  actionIndex: number;
  operator: string;
  inputValue: { itemId: string | null; itemType: string };
  outputValue: { itemId: string | null; itemType: string } | null;
  executionTime: { startedAt: string; completedAt: string; durationMs: number };
  status: string;
  statusMessage: string | null;
  context?: Record<string, unknown>;
}

export interface RuleRunMetricsDto {
  stepCount: number;
  successCount: number;
  failedCount: number;
  totalDurationMs: number;
  aiCostTotalUsd?: number | null;
}

/** List item from GET /rules/{ruleId}/logs (has id for detail fetch). */
export interface RuleActivityLog {
  id?: string;
  state: string;
  createdAt: number;
  itemId: string;
  itemName: string;
  itemType: string;
  jobMessage?: string;
}

export const getRuleLogDetail = async (
  ruleId: string,
  logId: string
): Promise<RuleRunLogDetail> => {
  const response = await get({ path: `/rules/${ruleId}/logs/${logId}` });
  return response.data;
};

/** Output context for one step (GET .../step-outputs). Usable as input context for the next step. */
export interface StepOutputContextDto {
  actionIndex: number;
  operator: string;
  output: Record<string, unknown> | null;
}

/** Output context variables for each step of a rule run (for rule builder / next-step input). */
export interface RuleRunStepOutputsDto {
  logId: string;
  steps: StepOutputContextDto[];
}

export const getRuleRunStepOutputs = async (
  ruleId: string,
  logId: string
): Promise<RuleRunStepOutputsDto> => {
  const response = await get({ path: `/rules/${ruleId}/logs/${logId}/step-outputs` });
  return response.data;
};

/** Aggregate metrics for a rule (recent runs). */
export interface RuleMetricsDto {
  completedCount: number;
  failedCount: number;
  inProgressCount: number;
  avgDurationMs: number | null;
  totalAiCostUsd: number | null;
  totalTokenUsage: number | null;
}

export const getRuleMetrics = async (ruleId: string): Promise<RuleMetricsDto> => {
  const response = await get({ path: `/rules/${ruleId}/metrics` });
  return response.data;
};
